import {
  buildQuizCopyQuery,
  type QuizCopyContext,
} from '@/shared/tracking/quizCopySource';

/**
 * 인증을 마친 사용자가 착지하는 자리.
 *
 * 인증은 별도 앱(`auth.zep.us`)에서 일어나고 그쪽이 아는 것은 하나뿐이다 — *"끝나면 이 상대
 * 경로로 보내 줘"*(`callbackUrl`). 그래서 **quiz-app 안에 착지점이 반드시 하나 필요하다.**
 * 예전에는 그 자리가 `/duplicate/{id}` 빌드 페이지였고, 사용자가 거기 눌러앉아 원래 보던
 * 화면(플레이)을 떠나는 것이 문제였다.
 *
 * 그래서 **머무는 화면이 아니라 통로**로 바꾼다. 이름은 시점(`copy-return`)이 아니라 동작으로
 * 짓는다 — `/duplicate/{id}`(빌드 경유 복사) 옆에 `/copy/{id}`(즉시 복사)가 서면 대체 관계가
 * 드러난다.
 *
 * ⚠️ `callbackUrl` 은 **상대 경로**여야 한다. auth-app 이 `isAllowedCallbackUrl` 로 절대 URL 을
 * 버리고 서비스 기본 목적지로 보낸다(`redirectGuards.utils.ts`).
 */
export const buildCopyPath = (
  sourceQuizsetId: string,
  context: QuizCopyContext,
) => `/copy/${sourceQuizsetId}${buildQuizCopyQuery(context)}`;

/**
 * 착지 탭이 자기를 연 탭(원탭)에 결과를 돌려줄 때 쓰는 메시지.
 *
 * **복사는 착지 탭이 한다.** 원탭은 인증 전 상태로 멈춰 있어 `useUser` 가 게스트다 —
 * `isGuestShareCopyBlocked` 에 필요한 계정 유형을 모른다. 방금 인증한 탭은 안다. 그래서 판정과
 * 복사를 거기서 끝내고 **원탭은 결과 표시만** 한다(원탭에 보류 상태를 두지 않는 이유).
 */
export const COPY_HANDOFF_MESSAGE_TYPE = 'quiz-copy-handoff' as const;

export type CopyHandoffMessage = {
  type: typeof COPY_HANDOFF_MESSAGE_TYPE;
  sourceQuizsetId: string;
} & (
  | { result: 'copied'; copiedQuizsetId: string }
  | { result: 'blocked' }
  | { result: 'failed' }
);

/**
 * 받은 값이 우리 메시지인지 좁힌다.
 *
 * `window.addEventListener('message')` 에는 확장 프로그램·다른 iframe 의 메시지도 함께 들어온다.
 * 오리진 검사는 수신부가 따로 하고(같은 오리진 + 내가 연 창), 여기서는 모양만 본다.
 */
export const isCopyHandoffMessage = (
  data: unknown,
): data is CopyHandoffMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === COPY_HANDOFF_MESSAGE_TYPE;
